import { Link } from 'react-router';
import type { ReportFilters } from '../../shared/schemas.ts';
import type { BandModeReport, RankEntry, TableReport } from '../../shared/types.ts';
import { CallLink } from './ui.tsx';

/** Raportin solusta käyttäjän QSO-listaan samoilla ehdoilla */
export function qsoLink(userId: string, filters: ReportFilters | null, col?: { band?: number; mode?: string }) {
  const p = new URLSearchParams();
  const bands = col?.band !== undefined ? [col.band] : (filters?.bands ?? []);
  const modes = col?.mode !== undefined ? [col.mode] : (filters?.modes ?? []);
  if (bands.length) p.set('bands', bands.join(','));
  if (modes.length) p.set('modes', modes.join(','));
  if (filters?.countries.length) p.set('countries', filters.countries.join(','));
  if (filters?.callSign) p.set('call', filters.callSign);
  return `/users/${encodeURIComponent(userId)}/qsos${p.size ? `?${p}` : ''}`;
}

const medal = (rank: unknown) => (rank === 1 ? 'medal-1' : rank === 2 ? 'medal-2' : rank === 3 ? 'medal-3' : '');

export function ReportTable({
  data,
  filters,
  linkCells,
  meId,
  stale = [],
  divisions = false,
}: {
  data: TableReport;
  filters: ReportFilters | null;
  /** kirjautuneille solut linkittyvät QSO-listaan */
  linkCells: boolean;
  meId?: string;
  stale?: string[];
  divisions?: boolean;
}) {
  const staleSet = new Set(stale.map((s) => s.toUpperCase()));
  if (!data.rows.length) return <div className="empty card">Taulukossa ei ole vielä tuloksia.</div>;
  return (
    <div className="table-wrap table-scroll">
      <table className="table">
        <thead>
          <tr>
            {data.columns.map((c) => (
              <th key={c.key} className={c.numeric ? 'num' : undefined}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => {
            const userId = String(r.userId);
            const breakRow = divisions && i > 0 && r.division !== data.rows[i - 1]!.division;
            return (
              <tr
                key={userId}
                className={[userId.toUpperCase() === meId?.toUpperCase() ? 'me' : '', breakRow ? 'division-break' : ''].join(' ')}
              >
                {data.columns.map((c) => {
                  const v = r[c.key];
                  if (c.key === 'userId') {
                    return (
                      <td key={c.key}>
                        <CallLink id={userId} />
                        {staleSet.has(userId.toUpperCase()) && (
                          <span className="faint" title="Ei päivittänyt kiinteitä arvojaan vertailupäivän jälkeen">
                            {' '}
                            *
                          </span>
                        )}
                      </td>
                    );
                  }
                  if (c.key === 'rank') {
                    return (
                      <td key={c.key} className={`rank ${medal(v)}`}>
                        {v}
                      </td>
                    );
                  }
                  const linkable = linkCells && v !== undefined && v !== null && c.key !== 'division' && c.key !== 'position';
                  return (
                    <td key={c.key} className={c.numeric ? 'num' : undefined}>
                      {linkable ? <Link to={qsoLink(userId, filters, { band: c.band, mode: c.mode })}>{v}</Link> : (v ?? '')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RankList({ title, entries, band, mode, linkCells, meId }: {
  title: string;
  entries: RankEntry[];
  band?: number;
  mode: string;
  linkCells: boolean;
  meId?: string;
}) {
  return (
    <div>
      <h3 style={{ marginBottom: 6 }}>{title}</h3>
      {entries.length ? (
        <table className="table compact">
          <tbody>
            {entries.map((e, i) => (
              <tr key={e.userId} className={e.userId.toUpperCase() === meId?.toUpperCase() ? 'me' : undefined}>
                <td className={`rank ${medal(i + 1)}`}>{i + 1}</td>
                <td>
                  <CallLink id={e.userId} />
                </td>
                <td className="num">
                  {linkCells ? <Link to={qsoLink(e.userId, null, { band, mode })}>{e.value}</Link> : e.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="faint small">–</div>
      )}
    </div>
  );
}

export function BandModeView({ data, linkCells, meId }: { data: BandModeReport; linkCells: boolean; meId?: string }) {
  return (
    <div className="bm-grid">
      {data.bands
        .filter((b) => b.cw.length || b.phone.length)
        .map((b) => (
          <div key={b.band} className="card bm-band">
            <h2>{b.label} m</h2>
            <div className="cols">
              <RankList title="CW" entries={b.cw} band={b.band} mode="CW" linkCells={linkCells} meId={meId} />
              <RankList title="PHONE" entries={b.phone} band={b.band} mode="PHONE" linkCells={linkCells} meId={meId} />
            </div>
          </div>
        ))}
      <div className="card bm-band">
        <h2>DIGI ALL</h2>
        <RankList title="Kaikki bandit 10–160 m" entries={data.digiAll} mode="DIGI" linkCells={linkCells} meId={meId} />
      </div>
    </div>
  );
}
